import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import type { DelegateCalendarEventRow } from '../../utils/executiveCalendarDelegatedService';
import { isGroupedEvent } from '../../utils/executiveCalendarDelegatedService';
import type { DayEventCluster } from '../../utils/executiveCalendarDayLayout';

type EacClusterPeekProps = {
  cluster: DayEventCluster;
  anchorRect: DOMRect;
  calendarColors: Map<string, string>;
  onSelectEvent: (ev: DelegateCalendarEventRow, rowRect: DOMRect) => void;
  onClose: () => void;
  /** Cancels the pending close when the pointer moves from pill into the card. */
  onPointerEnterCard: () => void;
};

function timeRange(ev: DelegateCalendarEventRow): string {
  if (!ev.start || !ev.end) return '';
  try {
    return `${format(parseISO(ev.start), 'h:mm')} – ${format(parseISO(ev.end), 'h:mm a')}`;
  } catch {
    return '';
  }
}

/** Readable expansion of a crowded time range, anchored beside the hovered pill. */
const EacClusterPeek = ({
  cluster,
  anchorRect,
  calendarColors,
  onSelectEvent,
  onClose,
  onPointerEnterCard,
}: EacClusterPeekProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const { width, height } = card.getBoundingClientRect();
    const margin = 8;
    let left = anchorRect.right + margin;
    if (left + width > window.innerWidth - margin) {
      left = anchorRect.left - width - margin;
    }
    left = Math.max(margin, left);
    let top = anchorRect.top;
    if (top + height > window.innerHeight - margin) {
      top = window.innerHeight - height - margin;
    }
    top = Math.max(margin, top);
    setPos({ top, left });
  }, [anchorRect, cluster]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const day = new Date(cluster.startMs);

  return (
    <div
      ref={cardRef}
      className="eac-cluster-peek"
      style={{
        top: pos?.top ?? anchorRect.top,
        left: pos?.left ?? anchorRect.right + 8,
        visibility: pos ? 'visible' : 'hidden',
      }}
      role="dialog"
      aria-label="Events in this time range"
      onMouseEnter={onPointerEnterCard}
      onMouseLeave={onClose}
    >
      <div className="eac-cluster-peek-head">
        <span>{format(day, 'EEE, MMM d')}</span>
        <span className="eac-cluster-peek-count">
          {cluster.events.length} event{cluster.events.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="eac-cluster-peek-list">
        {cluster.events.map(ev => (
          <button
            key={`${ev.calendarId}-${ev.id}`}
            type="button"
            className="eac-cluster-peek-row"
            onClick={e => onSelectEvent(ev, e.currentTarget.getBoundingClientRect())}
          >
            <span
              className="eac-cluster-peek-dot"
              style={{ background: calendarColors.get(ev.calendarId) || '#bfdbfe' }}
              aria-hidden
            />
            <span className="eac-cluster-peek-body">
              <span className="eac-cluster-peek-title">
                {isGroupedEvent(ev) && <span className="eac-group-badge">Trip · </span>}
                {ev.title}
              </span>
              <span className="eac-cluster-peek-time">{timeRange(ev)}</span>
              {ev.location && <span className="eac-cluster-peek-loc">{ev.location}</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default EacClusterPeek;
